import { isAnyOf } from '@reduxjs/toolkit';
import type { TypedStartListening } from '@reduxjs/toolkit';
import type { ApolloClient } from '@apollo/client';
import { toast } from 'components/Toaster';
import logger from 'util/logger';
import {
  SectionFragment,
  SectionFragmentDoc,
} from 'components/fragments.generated';
import {
  BulkUpdateSectionEditorsDocument,
  BulkUpdateSectionEditorsMutation,
  BulkUpdateSectionEditorsMutationVariables,
  UpdateSectionEditorDocument,
  UpdateSectionEditorMutation,
  UpdateSectionEditorMutationVariables,
} from 'components/ScriptStudio/Sections/operation.generated';
import listenerMiddleware from './listenerMiddleware';
import {
  clearDraft,
  clearDrafts,
  clearProjectDrafts,
  setFormattedTextDraft,
  setFormattedTextDrafts,
  setProjectDrafts,
  setSectionDrafts,
  setTextDraft,
} from './ScriptStudioBuffersSlice';
import {
  clearSectionSaveErrors,
  setSectionSaveErrors,
} from './ScriptStudioSlice';
import type { AppDispatch, AppState } from './index';

/** Server cap on sections per bulkUpdateSections call. */
export const BULK_UPDATE_SECTIONS_LIMIT = 200;

export const SAVE_FAILED_MESSAGE =
  'Unable to save your script changes. Please refresh and try again.';

export interface SectionEditorSave {
  id: string;
  /** The draft's formatted text, before it's normalized for the server */
  editor: string;
}

interface SaveResult extends SectionEditorSave {
  error: string | null;
}

interface StoreApi {
  dispatch: AppDispatch;
  getState: () => AppState;
}

let client: ApolloClient<unknown> | null = null;
let enabled = false;

/** Sections with a save on the wire; each section has at most one. */
const inFlight = new Set<string>();
/** The latest text waiting on a section's in-flight save. */
const queued = new Map<string, string>();

/**
 * Turns saving on or off. Studio calls this with its Apollo client and the
 * `section-editor-sync` flag; until then nothing is saved.
 */
export function configureSectionEditorSync(options: {
  client: ApolloClient<unknown> | null;
  enabled: boolean;
}) {
  client = options.client;
  enabled = options.enabled;
}

export const isSectionEditorSyncEnabled = () => enabled && client !== null;

/** Drops all save state. For tests. */
export function resetSectionEditorSync() {
  client = null;
  enabled = false;
  inFlight.clear();
  queued.clear();
}

const readCachedSection = (
  apolloClient: ApolloClient<unknown>,
  id: string
): SectionFragment | null => {
  try {
    return apolloClient.cache.readFragment<SectionFragment>({
      id: apolloClient.cache.identify({ __typename: 'Section', id }),
      fragment: SectionFragmentDoc,
      fragmentName: 'SectionFragment',
    });
  } catch {
    return null;
  }
};

/**
 * `section.editor` only holds text that still needs a render: text equal to
 * the current take (or empty, without a take) is saved as null.
 */
export const toServerEditor = (
  section: Pick<SectionFragment, 'currentTake'> | null,
  editor: string
): string | null => {
  const take = section?.currentTake;
  if (take) return editor === take.formattedText ? null : editor;
  return editor === '' ? null : editor;
};

/**
 * Users always see the generic message: the server's wording (validation,
 * internal errors) isn't written for them. Its code and message are logged.
 */
const saveError = (
  response:
    | { success: boolean; code: string; message: string }
    | null
    | undefined,
  ids: string[]
): string | null => {
  if (response?.success) return null;
  logger.error(
    'Section editor save failed',
    ids,
    response?.code,
    response?.message
  );
  return SAVE_FAILED_MESSAGE;
};

const saveRequestError = (error: unknown, ids: string[]): string => {
  logger.error('Section editor save failed', ids, error);
  return SAVE_FAILED_MESSAGE;
};

const chunk = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

async function sendSingle(
  apolloClient: ApolloClient<unknown>,
  save: SectionEditorSave
): Promise<SaveResult[]> {
  let error: string | null;
  try {
    const { data } = await apolloClient.mutate<
      UpdateSectionEditorMutation,
      UpdateSectionEditorMutationVariables
    >({
      mutation: UpdateSectionEditorDocument,
      variables: {
        inputs: {
          id: save.id,
          editor: toServerEditor(
            readCachedSection(apolloClient, save.id),
            save.editor
          ),
        },
      },
    });
    error = saveError(data?.updateSection, [save.id]);
  } catch (requestError) {
    error = saveRequestError(requestError, [save.id]);
  }
  return [{ ...save, error }];
}

async function sendBulk(
  apolloClient: ApolloClient<unknown>,
  saves: SectionEditorSave[]
): Promise<SaveResult[]> {
  const results = await Promise.all(
    chunk(saves, BULK_UPDATE_SECTIONS_LIMIT).map(async batch => {
      let error: string | null;
      try {
        const { data } = await apolloClient.mutate<
          BulkUpdateSectionEditorsMutation,
          BulkUpdateSectionEditorsMutationVariables
        >({
          mutation: BulkUpdateSectionEditorsDocument,
          variables: {
            inputs: {
              sections: batch.map(({ id, editor }) => ({
                id,
                editor: toServerEditor(
                  readCachedSection(apolloClient, id),
                  editor
                ),
              })),
            },
          },
        });
        error = saveError(
          data?.bulkUpdateSections,
          batch.map(({ id }) => id)
        );
      } catch (requestError) {
        error = saveRequestError(
          requestError,
          batch.map(({ id }) => id)
        );
      }
      return batch.map(save => ({ ...save, error }));
    })
  );
  return results.flat();
}

function finishSaves(results: SaveResult[], bulk: boolean, api: StoreApi) {
  results.forEach(({ id }) => inFlight.delete(id));

  const saved = results.filter(({ error }) => error === null);
  if (saved.length > 0) {
    api.dispatch(clearSectionSaveErrors(saved.map(({ id }) => id)));
    // The draft stays: `useSectionView` clears it once the section it renders
    // shows the saved text. Clearing here can beat that render, so the editor
    // briefly gets the previous `section.editor` and reverts newer typing.
  }

  const failedByError = new Map<string, string[]>();
  results.forEach(({ id, error }) => {
    if (error !== null) {
      failedByError.set(error, [...(failedByError.get(error) ?? []), id]);
    }
  });
  failedByError.forEach((sectionIds, error) => {
    api.dispatch(setSectionSaveErrors({ sectionIds, error }));
  });
  // Bulk edits can touch sections that are offscreen
  if (bulk && failedByError.size > 0) {
    toast({ message: SAVE_FAILED_MESSAGE, type: 'error' });
  }

  const next = results.flatMap(({ id }) => {
    const editor = queued.get(id);
    if (editor === undefined) return [];
    queued.delete(id);
    return [{ id, editor }];
  });
  if (next.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    saveSectionEditors(next, { bulk: next.length > 1 }, api);
  }
}

/**
 * Saves section text to `section.editor`, one save in flight per section.
 * Text for a section that is already saving waits for that save and only the
 * latest waiting text is sent, so an older save never lands after a newer one.
 * @param options.bulk save through `bulkUpdateSections` (a failure also
 * toasts), otherwise `updateSection`
 */
export function saveSectionEditors(
  saves: SectionEditorSave[],
  { bulk }: { bulk: boolean },
  api: StoreApi
) {
  const apolloClient = client;
  if (!enabled || !apolloClient) return;

  const ready = new Map<string, string>();
  saves.forEach(({ id, editor }) => {
    if (inFlight.has(id)) {
      queued.set(id, editor);
    } else {
      ready.set(id, editor);
    }
  });
  if (ready.size === 0) return;

  const toSend = Array.from(ready, ([id, editor]) => ({ id, editor }));
  toSend.forEach(({ id }) => inFlight.add(id));

  const request = bulk
    ? sendBulk(apolloClient, toSend)
    : Promise.all(toSend.map(save => sendSingle(apolloClient, save))).then(
        results => results.flat()
      );
  request.then(results => finishSaves(results, bulk, api));
}

const isSingleDraftAction = isAnyOf(setTextDraft, setFormattedTextDraft);
const isBulkDraftAction = isAnyOf(
  setFormattedTextDrafts,
  setSectionDrafts,
  setProjectDrafts
);
/**
 * Drafts dropped for the backend's text (take switch or delete, section
 * delete, project delete). clearTextDraft is left out: it fires once a draft
 * matches the saved text.
 */
const isClearDraftAction = isAnyOf(clearDraft, clearDrafts, clearProjectDrafts);

const startAppListening =
  listenerMiddleware.startListening as TypedStartListening<
    AppState,
    AppDispatch
  >;

/**
 * Saves script drafts to `section.editor` as they're set, so the server holds
 * the latest script before a take is rendered. Drafts already wait out the
 * editor's typing debounce, so they're saved as they arrive:
 * - single section edits (typing, cue and voice changes) through updateSection
 * - bulk edits (bulk cues, replacements, import, translate) through
 *   bulkUpdateSections, unless they're marked `skipSave` (e.g. a cue slider
 *   drag saves once, on release)
 * - dropping a draft for the backend's text drops its waiting save and error
 *
 * Registered once with the store; does nothing until
 * `configureSectionEditorSync` turns it on.
 * @see `section-editor-sync` feature flag
 */
export function startSectionEditorSync() {
  return startAppListening({
    matcher: isAnyOf(
      isSingleDraftAction,
      isBulkDraftAction,
      isClearDraftAction
    ),
    effect: (action, api) => {
      if (!isSectionEditorSyncEnabled()) return;

      if (isSingleDraftAction(action)) {
        const { id, formattedText } = action.payload;
        saveSectionEditors(
          [{ id, editor: formattedText }],
          { bulk: false },
          api
        );
        return;
      }

      if (isBulkDraftAction(action)) {
        if (
          (setFormattedTextDrafts.match(action) ||
            setProjectDrafts.match(action)) &&
          action.payload.skipSave
        ) {
          return;
        }
        saveSectionEditors(
          action.payload.sections.map(({ id, formattedText }) => ({
            id,
            editor: formattedText,
          })),
          { bulk: true },
          api
        );
        return;
      }

      let ids: string[] = [];
      if (clearDraft.match(action)) {
        ids = [action.payload];
      } else if (clearDrafts.match(action)) {
        ids = action.payload;
      } else if (clearProjectDrafts.match(action)) {
        ids = Object.entries(api.getOriginalState().scriptStudioBuffers.byId)
          .filter(([, draft]) => draft.projectId === action.payload)
          .map(([id]) => id);
      }
      ids.forEach(id => queued.delete(id));
      api.dispatch(clearSectionSaveErrors(ids));
    },
  });
}
